"use client";

import type { ReactNode } from "react";
import { useUserProfile } from "@/state/globalStore";
import {
  VStack,
  Text,
  NumberInput,
  HStack,
  Button,
  Stack,
  ColorPicker,
  parseColor,
  Portal,
  Box,
  Heading,
} from "@chakra-ui/react";
import type {
  DateConvention,
  DateDetail,
  TimelineFraming,
  SupportedLanguage,
  SectionNamingStyle,
  PrintSettings,
  ResumeReturnFormat,
  VisuallyEmphatize,
} from "@/generated/fastapi";
import SettingsSkeleton from "./SettingsSkeleton";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericToggle from "@/shared/ui/controls/GenericToggle";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import {
  getRelevantPrintSettingsConfig,
  usePrintSettingsActions,
} from "@/features/profile";
import usePrintSettingsUI from "../hooks/usePrintSettingsUI";
import WithLongPress from "@/shared/ui/gestures/WithLongPress";
import CustomSectionNaming from "./CustomSectionNaming";

// ---------------------------------
// Component: PrintSettings
// ---------------------------------

function PrintSettingBlock({
  title,
  description,
  children,
  hint,
}: {
  title: string;
  description: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <Box
      borderWidth="1px"
      borderColor="moveon.surface.border"
      borderRadius="xl"
      bg="moveon.surface.bg"
      px={{ base: "3", md: "4" }}
      py={{ base: "3", md: "4" }}
    >
      <VStack align="stretch" spaceY="3">
        <Box>
          <Heading size="sm">{title}</Heading>
          <Text fontSize="sm" color="moveon.text.muted" mt="1">
            {description}
          </Text>
          {hint ? <Box mt="2">{hint}</Box> : null}
        </Box>
        {children}
      </VStack>
    </Box>
  );
}

export default function PrintSettings() {
  const { userProfile } = useUserProfile();
  const {
    dateConventionItems,
    dateDetailItems,
    languageItems,
    timelineFramingItems,
    resumeReturnFormatItems,
    sectionNamingStyleItems,
    visuallyEmphatizeItems,
  } = useOptionItems();
  const { setCustomSectionEnabled, updateCustomSection } =
    usePrintSettingsActions();

  // Local print settings state and handlers
  const {
    printSettings,
    setPrintSettings,
    resetKey,
    handleUpdate,
    handleReset,
    handlePublishForDaysChange,
  } = usePrintSettingsUI();

  if (userProfile.isLoading) {
    return <SettingsSkeleton />;
  }

  // Keyboard handler (compatible with GenericEditable)
  function handleKeyDown(e: React.KeyboardEvent, submitFunction: () => void) {
    if (e.key === "Enter") {
      e.preventDefault();
      submitFunction();
    }
  }

  // Layout constants
  const firstColMinW = "72"; // Minimum width for first column
  const showCustomSectionNamingOptions =
    printSettings.section_naming === "custom";
  const activePrintLanguage =
    printSettings.print_language ??
    userProfile.data.print_settings.print_language;
  const activePrintLanguageLabel =
    languageItems.find((item) => item.value === activePrintLanguage)?.label ??
    activePrintLanguage.toUpperCase();

  return (
    <VStack align="stretch" spaceY={4}>
      <GenericDrawerInfo
        description="Control the format, language, public link, and visual treatment used when MoveOn generates profile exports."
        items={[
          { label: activePrintLanguageLabel, colorPalette: "teal" },
          {
            label: printSettings.return_format?.toUpperCase() ?? "Format open",
            colorPalette: printSettings.return_format ? "blue" : "gray",
          },
          {
            label: printSettings.link_moveon
              ? "Public link enabled"
              : "Public link off",
            colorPalette: printSettings.link_moveon ? "green" : "gray",
          },
        ]}
      />

      {/* Control section with Reset button */}
      <HStack justify="flex-end">
        <WithLongPress
          onLongPress={handleReset}
          longPressHint="Press long to reset all print settings to defaults"
          enabled={true}
          threshold="critical"
        >
          <Button
            variant="ghost"
            // onClick={handleReset} --- onClick disabled in favor of long press action
            color="moveon.danger"
          >
            <GenericIcon icon="reset" />
            Reset
          </Button>
        </WithLongPress>
      </HStack>

      <PrintSettingBlock
        title="Print Language"
        description="Choose the language used in printed CVs and profile exports."
        hint={
          <HStack
            align="center"
            gap="2"
            color="moveon.text.muted"
            flexWrap="wrap"
          >
            <GenericIcon
              icon={activePrintLanguage}
              size="sm"
              humanReadable={`${activePrintLanguageLabel} flag`}
            />
            <Text fontSize="sm">
              Current language: {activePrintLanguageLabel}.
            </Text>
          </HStack>
        }
      >
        <Box width="full">
          <GenericSelector<SupportedLanguage>
            key={`print-language-${resetKey}`}
            previewItems={languageItems}
            stateObject={
              printSettings.print_language ? [printSettings.print_language] : []
            }
            stateObjectSetter={(newSelection) => {
              const newLanguage = newSelection[0];
              handleUpdate({ print_language: newLanguage });
            }}
            mode="select"
            placeholder="Select print language"
            disableClear={true}
          />
        </Box>
      </PrintSettingBlock>

      {/* timestamp_resume */}
      <GenericToggle
        key={`timestamp-resume-${resetKey}`}
        label="Timestamp CV"
        description="Controls whether the CV prints with a timestamp."
        value={printSettings.timestamp_resume || false}
        onChange={(newValue) => handleUpdate({ timestamp_resume: newValue })}
        minW={firstColMinW}
      />

      {/* sign_resume */}
      <GenericToggle
        key={`sign-resume-${resetKey}`}
        label="Sign CV"
        description="Controls whether the CV gets a MoveOn Product signature when printed."
        value={printSettings.sign_resume || false}
        onChange={(newValue) => handleUpdate({ sign_resume: newValue })}
        minW={firstColMinW}
      />

      {/* link_moveon */}
      <GenericToggle
        key={`link-moveon-${resetKey}`}
        label="Link MoveOn"
        description="Controls whether profile exports include a public MoveOn link you can share with others. This requires fully reflected thoughts."
        value={printSettings.link_moveon || false}
        onChange={(newValue) => handleUpdate({ link_moveon: newValue })}
        minW={firstColMinW}
      />

      {/* publish_for_days */}
      {printSettings.link_moveon && (
        <PrintSettingBlock
          title="Public Link Duration"
          description="Choose how many days the public MoveOn link should stay available."
        >
          <Box width="full">
            <NumberInput.Root
              key={`publish-days-${resetKey}`}
              value={
                printSettings.publish_for_days !== undefined
                  ? String(printSettings.publish_for_days)
                  : "3"
              }
              min={1}
              max={14}
              step={1}
              spinOnPress={false}
              onChange={(event) => {
                const valueString = (event.target as HTMLInputElement).value;
                const newValue = parseInt(valueString, 10);
                handlePublishForDaysChange(newValue);
              }}
              disabled={!printSettings.link_moveon}
            >
              <HStack gap="2" alignItems="center" width="fit-content">
                <NumberInput.DecrementTrigger asChild>
                  <Button
                    aria-label="Decrease days"
                    variant="outline"
                    size="sm"
                    disabled={
                      !printSettings.link_moveon ||
                      (printSettings.publish_for_days || 0) <= 1
                    }
                    onClick={() => {
                      if (printSettings.link_moveon) {
                        handlePublishForDaysChange(
                          (printSettings.publish_for_days || 0) - 1,
                        );
                      }
                    }}
                  >
                    <GenericIcon icon="minus" />
                  </Button>
                </NumberInput.DecrementTrigger>
                <NumberInput.ValueText
                  textAlign="center"
                  fontSize="lg"
                  minW="8"
                />
                <NumberInput.IncrementTrigger asChild>
                  <Button
                    aria-label="Increase days"
                    variant="outline"
                    size="sm"
                    disabled={
                      !printSettings.link_moveon ||
                      (printSettings.publish_for_days || 0) >= 14
                    }
                    onClick={() => {
                      if (printSettings.link_moveon) {
                        handlePublishForDaysChange(
                          (printSettings.publish_for_days || 0) + 1,
                        );
                      }
                    }}
                  >
                    <GenericIcon icon="plus" />
                  </Button>
                </NumberInput.IncrementTrigger>
              </HStack>
            </NumberInput.Root>
          </Box>
        </PrintSettingBlock>
      )}

      {/* timestamp_moveon_link */}
      {printSettings.link_moveon && (
        <GenericToggle
          key={`timestamp-moveon-link-${resetKey}`}
          label="Timestamp MoveOn link"
          description="Controls whether MoveOn link will print with a timestamp until it is publically accessible."
          value={printSettings.timestamp_moveon_link || false}
          onChange={(newValue) =>
            handleUpdate({ timestamp_moveon_link: newValue })
          }
          disabled={!printSettings.link_moveon}
          minW={firstColMinW}
        />
      )}

      <PrintSettingBlock
        title="Return Format"
        description="Choose the file format used when profile exports are generated."
      >
        <Box width="full">
          <GenericSelector<ResumeReturnFormat>
            key={`resume-return-format-${resetKey}`}
            previewItems={resumeReturnFormatItems}
            stateObject={
              printSettings.return_format ? [printSettings.return_format] : []
            }
            stateObjectSetter={(newSelection) => {
              const newFormat = newSelection[0];
              handleUpdate({ return_format: newFormat });
            }}
            mode="select"
            placeholder="Select profile export format"
            disableClear={true}
          />
        </Box>
      </PrintSettingBlock>

      <PrintSettingBlock
        title="Visual Emphasis"
        description="Define how strongly the printed layout should emphasize key content."
      >
        <Box width="full">
          <GenericSelector<VisuallyEmphatize>
            key={`visually-emphatize-${resetKey}`}
            previewItems={visuallyEmphatizeItems}
            stateObject={
              printSettings.visually_emphatize
                ? [printSettings.visually_emphatize]
                : []
            }
            stateObjectSetter={(newSelection) => {
              const newEmphatize = newSelection[0];
              handleUpdate({ visually_emphatize: newEmphatize });
            }}
            mode="select"
            placeholder="Select visual emphasis"
            disableClear={true}
            showDescription={true}
          />
        </Box>
      </PrintSettingBlock>

      <PrintSettingBlock
        title="Primary Color"
        description="Set the accent color used across printed CVs and profile exports."
      >
        <Stack gap="4" align="stretch" width="full">
          <ColorPicker.Root
            key={`primary-color-${resetKey}`}
            defaultValue={parseColor(printSettings.primary_color || "#000000")}
            onValueChangeEnd={(e) => {
              const newColor = e.value;
              handleUpdate({ primary_color: newColor.toString("hex") });
            }}
          >
            <ColorPicker.HiddenInput />
            <ColorPicker.Control>
              <ColorPicker.Input />
              <ColorPicker.Trigger />
            </ColorPicker.Control>
            <Portal>
              <ColorPicker.Positioner>
                <ColorPicker.Content
                  // @ts-ignore Works but TS complains
                  zIndex={9999} // above other elements (drawers)
                >
                  <ColorPicker.Area />
                  <HStack>
                    <ColorPicker.EyeDropper size="xs" variant="outline" />
                    <ColorPicker.Sliders />
                  </HStack>
                </ColorPicker.Content>
              </ColorPicker.Positioner>
            </Portal>
          </ColorPicker.Root>
        </Stack>
      </PrintSettingBlock>

      <PrintSettingBlock
        title="Timeline Framing"
        description="Decide how much time context appears in profile exports. Hidden removes dates, numerical exposes detailed controls, and the other modes follow system defaults."
      >
        <Box width="full">
          <GenericSelector<TimelineFraming>
            key={`timeline-framing-${resetKey}`}
            previewItems={timelineFramingItems}
            stateObject={
              printSettings.timeline_framing
                ? [printSettings.timeline_framing]
                : []
            }
            stateObjectSetter={(newSelection) => {
              const newFraming = newSelection[0];
              handleUpdate({ timeline_framing: newFraming });
            }}
            mode="select"
            placeholder="Select timeline framing"
            showDescription={true}
            disableClear={true}
          />
        </Box>
      </PrintSettingBlock>

      {printSettings.timeline_framing === "numerical" && (
        <>
          <PrintSettingBlock
            title="Date Detail"
            description="Choose how granular the printed timeline should be."
          >
            <Box width="full">
              <GenericSelector<DateDetail>
                key={`date-detail-${resetKey}`}
                previewItems={dateDetailItems}
                stateObject={
                  printSettings.date_detail ? [printSettings.date_detail] : []
                }
                stateObjectSetter={(newSelection) => {
                  const newDetail = newSelection[0];
                  handleUpdate({ date_detail: newDetail });
                }}
                mode="select"
                placeholder="Select date detail level"
                disableClear={true}
              />
            </Box>
          </PrintSettingBlock>

          <PrintSettingBlock
            title="Date Convention"
            description="Choose the date formatting convention used in printed timelines."
          >
            <Box width="full">
              <GenericSelector<DateConvention>
                key={`date-convention-${resetKey}`}
                previewItems={dateConventionItems}
                stateObject={
                  printSettings.date_convention
                    ? [printSettings.date_convention]
                    : []
                }
                stateObjectSetter={(newSelection) => {
                  const newConvention = newSelection[0];
                  handleUpdate({ date_convention: newConvention });
                }}
                mode="select"
                placeholder="Select date convention"
                disableClear={true}
              />
            </Box>
          </PrintSettingBlock>
        </>
      )}

      <PrintSettingBlock
        title="Section Naming"
        description="Decide how section titles are named in printed documents."
      >
        <Box width="full">
          <GenericSelector<SectionNamingStyle>
            key={`section-naming-style-${resetKey}`}
            previewItems={sectionNamingStyleItems}
            stateObject={
              printSettings.section_naming ? [printSettings.section_naming] : []
            }
            stateObjectSetter={(newSelection) => {
              const newStyle = newSelection[0];
              handleUpdate({ section_naming: newStyle });
            }}
            mode="select"
            placeholder="Select section naming style"
            disableClear={true}
            showDescription={true}
          />
        </Box>
      </PrintSettingBlock>

      {showCustomSectionNamingOptions && (
        <CustomSectionNaming
          printSettings={printSettings}
          resetKey={resetKey}
          relevantPersonalDataConfig={getRelevantPrintSettingsConfig(
            printSettings,
            printSettings.print_language,
          )}
          setPrintSettingsAction={setPrintSettings}
          handleKeyDownAction={handleKeyDown}
          setCustomSectionEnabledAction={setCustomSectionEnabled}
          updateCustomSectionAction={updateCustomSection}
        />
      )}
    </VStack>
  );
}
